// ============================================================
// TERROR ENGINE — ZONE ENGINE
// Shared tile math, camera, walkability, and portrait detection.
// Every walkable zone (Plaza, Workshop, FactionHall, Dungeon)
// imports from here. Configure per zone via ZoneConfig.
//
// Usage:
//   import { calcTile, calcCamOffset, isPortrait, isWalkable } from "./engine/ZoneEngine";
//
// To define walkability for a zone, pass your map array and
// optional wall tile type. The engine handles the rest.
// ============================================================

// ── Portrait detection ────────────────────────────────────────
export function isPortrait() {
  return typeof window !== "undefined" && window.innerHeight > window.innerWidth;
}

// ── Tile size — responsive, fits screen ──────────────────────
// Each zone can override MIN/MAX if needed
export function calcTile(minTile = 36, maxTile = 64) {
  if (typeof window === "undefined") return 48;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Target: ~20 tiles wide on landscape, ~14 on portrait
  const byWidth  = Math.floor(vw  / 20);
  const byHeight = Math.floor(vh  / 14);
  const base     = Math.min(byWidth, byHeight);
  return Math.max(minTile, Math.min(maxTile, base));
}

// ── Camera offset — keeps player centered, clamps to map edges
export function calcCamOffset(playerX, playerY, tile, cols, rows) {
  if (typeof window === "undefined") return { x: 0, y: 0 };
  const vw   = window.innerWidth;
  const vh   = window.innerHeight;
  const mapW = cols * tile;
  const mapH = rows * tile;
  const rawX = playerX * tile + tile / 2 - vw / 2;
  const rawY = playerY * tile + tile / 2 - vh / 2;
  return {
    x: Math.max(0, Math.min(Math.max(0, mapW - vw), rawX)),
    y: Math.max(0, Math.min(Math.max(0, mapH - vh), rawY)),
  };
}

// ── Tile types ────────────────────────────────────────────────
// Shared across all zones. Zones can add their own on top.
export const T = {
  FLOOR:  0,
  WALL:   1,
  VOID:   2,
  WATER:  3,
  DOOR:   4,
  WARP:   5,
};

// ── Generic walkability ───────────────────────────────────────
// Pass in your map array, and optional set of unwalkable tile types.
// Zones can use this directly or write their own isWalkable.
export function isWalkable(x, y, map, unwalkable = [T.WALL, T.VOID]) {
  if (!map || y < 0 || y >= map.length || x < 0 || x >= map[0].length) return false;
  return !unwalkable.includes(map[y][x]);
}

// ── Map builder helper ────────────────────────────────────────
// Build a blank map of given size, optionally with a border wall.
export function buildMap(cols, rows, fill = T.FLOOR, wallBorder = true) {
  const map = Array.from({ length: rows }, (_, y) =>
    Array.from({ length: cols }, (_, x) => {
      if (wallBorder && (x === 0 || y === 0 || x === cols - 1 || y === rows - 1)) return T.WALL;
      return fill;
    })
  );
  return map;
}

// ── Distance helper ───────────────────────────────────────────
export function tileDistance(ax, ay, bx, by) {
  return Math.max(Math.abs(ax - bx), Math.abs(ay - by)); // Chebyshev distance
}

// ── Spawn clamping ────────────────────────────────────────────
export function clampSpawn(x, y, cols, rows) {
  return {
    x: Math.max(1, Math.min(cols - 2, x)),
    y: Math.max(1, Math.min(rows - 2, y)),
  };
}
